import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import feeService from "../../services/feeService";
import PrintInstructions from "../../components/common/PrintInstructions";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const MONTHS = [
    { v: 1, l: "January" }, { v: 2, l: "February" }, { v: 3, l: "March" },
    { v: 4, l: "April" }, { v: 5, l: "May" }, { v: 6, l: "June" },
    { v: 7, l: "July" }, { v: 8, l: "August" }, { v: 9, l: "September" },
    { v: 10, l: "October" }, { v: 11, l: "November" }, { v: 12, l: "December" },
];

const MODE_COLORS = {
    Cash: "#16a34a", Cheque: "#003b67", NEFT: "#d97706", UPI: "#7c3aed", DD: "#6b7280",
};

export default function MonthlyReport() {
    const navigate = useNavigate();
    const { academicYear, role } = useApp();
    const feeBasePath = role === "clerk" ? "/clerk/fees" : role === "accountant" ? "/accountant/fees" : "/fees";

    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);

    // Year options
    const yearOptions = [];
    for (let y = now.getFullYear() + 1; y >= 2020; y--) yearOptions.push(y);

    const fetchReport = async () => {
        if (!academicYear?._id) { toast.error("Select an academic year first."); return; }
        setLoading(true);
        try {
            const res = await feeService.getMonthlyReport({
                month,
                year,
                academicYear: academicYear._id,
            });
            setReport(res.data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch when month/year/academicYear changes
    useEffect(() => {
        if (academicYear?._id) fetchReport();
        // eslint-disable-next-line
    }, [month, year, academicYear]);

    const handlePrint = () => window.print();

    return (
        <div>
            <div className="page-header no-print">
                <div>
                    <h1 className="page-title">Monthly Fee Collection Report</h1>
                    <p className="page-subtitle">
                        {academicYear ? academicYear.label : "No academic year selected"}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate(feeBasePath)}>← Back</button>
                    {report && (
                        <button className="btn btn-success" onClick={handlePrint}>🖨 Print Report</button>
                    )}
                </div>
            </div>

            {/* Print banner */}
            {report && <PrintInstructions />}

            {/* ── Filter bar ───────────────────────────────────────────────────── */}
            <div className="card no-print" style={{ marginBottom: "1.25rem" }}>
                <div className="card-body" style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Month</label>
                        <select className="form-control" style={{ width: 145 }}
                            value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                            {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                        </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Year</label>
                        <select className="form-control" style={{ width: 110 }}
                            value={year} onChange={(e) => setYear(Number(e.target.value))}>
                            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <button className="btn btn-primary" onClick={fetchReport} disabled={loading || !academicYear?._id}>
                        {loading ? "Loading…" : "Generate"}
                    </button>
                </div>
            </div>

            {!academicYear && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", fontSize: 13, color: "#92400e" }}>
                    ⚠️ Select an academic year from the Dashboard.
                </div>
            )}

            {loading && <div className="loading-center"><div className="spinner" />Generating report…</div>}

            {report && !loading && (() => {
                const grandTotal = Number(report.grandTotal ?? report.totalCollected ?? 0);
                const totalReceipts = Number(report.totalReceipts ?? report.receiptCount ?? (report.receipts?.length || 0));
                const monthName = report.monthName || (MONTHS.find((m) => m.v === Number(month))?.l || `Month ${month}`);
                const reportYear = report.year || year;

                const modeEntries = Array.isArray(report.modeBreakdownList)
                    ? report.modeBreakdownList.map((m) => [m.mode, Number(m.amount) || 0])
                    : Object.entries(report.modeBreakdown || report.modeMap || {}).map(([k, v]) => [k, Number(v) || 0]);

                return (
                    <div className="print-area monthly-fee-report-print">
                        <style>{`
                            @media print {
                                @page {
                                    size: A4 portrait;
                                    margin: 0.3in;
                                }
                                html, body {
                                    height: auto !important;
                                    overflow: visible !important;
                                }
                                .monthly-fee-report-print {
                                    width: 100% !important;
                                    max-width: 100% !important;
                                    box-shadow: none !important;
                                }
                                .monthly-fee-report-print table {
                                    font-size: 11px !important;
                                }
                                .monthly-fee-report-print tr {
                                    page-break-inside: avoid;
                                }
                                .monthly-fee-report-print thead {
                                    display: table-header-group;
                                }
                            }
                        `}</style>

                        {/* ── Report Header (Print only) ────────────────────────────── */}
                        <div className="only-print" style={{ marginBottom: "1.25rem" }}>
                            <CollegePrintHeader
                                layout="banner"
                                logoSize={56}
                                title={`Monthly Fee Collection Report — ${monthName} ${reportYear}`}
                                subtitle={`Academic Year: ${academicYear?.label || "—"}`}
                                rightContent={
                                    <div style={{ fontSize: "11px", color: "#475569" }}>
                                        Generated: {new Date().toLocaleDateString("en-IN")}
                                    </div>
                                }
                            />
                        </div>

                        {/* ── Summary cards ─────────────────────────────────────────── */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }} className="no-print">
                            <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #16a34a" }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: "#16a34a" }}>{inr(grandTotal)}</div>
                                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Total Collected</div>
                            </div>
                            <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #003b67" }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: "#003b67" }}>{totalReceipts}</div>
                                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Receipts Issued</div>
                            </div>
                            {/* Mode breakdown chips */}
                            {modeEntries.map(([mode, amt]) => (
                                <div key={mode} className="card" style={{ padding: "1rem", borderLeft: `4px solid ${MODE_COLORS[mode] || "#6b7280"}` }}>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: MODE_COLORS[mode] || "#6b7280" }}>{inr(amt)}</div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{mode}</div>
                                </div>
                            ))}
                        </div>

                        {/* ── Report heading (screen) ───────────────────────────────── */}
                        <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
                                {monthName} {reportYear} — {totalReceipts} receipt(s)
                            </h2>
                            <span style={{ fontSize: 14, fontWeight: 700, color: "#16a34a" }}>
                                Total: {inr(grandTotal)}
                            </span>
                        </div>

                        {/* ── Receipts table ────────────────────────────────────────── */}
                        {!report.receipts || report.receipts.length === 0 ? (
                            <div className="empty-state">
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                        d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                                </svg>
                                <p>No receipts found for {monthName} {reportYear}.</p>
                            </div>
                        ) : (
                            <div className="card">
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Receipt No.</th>
                                                <th>Student</th>
                                                <th>Course</th>
                                                <th>Installment</th>
                                                <th>Amount</th>
                                                <th>Date</th>
                                                <th>Mode</th>
                                                <th>Ref / Cheque</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.receipts.map((r, i) => {
                                                const courseStr = typeof r.student?.course === "object"
                                                    ? (r.student?.course?.name || r.student?.course?.shortName)
                                                    : (r.student?.courseAdmitted || r.student?.course || "—");
                                                return (
                                                    <tr key={r._id}>
                                                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{i + 1}</td>
                                                        <td style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#003b67" }}>
                                                            {r.receiptNo}
                                                        </td>
                                                        <td>
                                                            <div style={{ fontWeight: 500, fontSize: 13 }}>{r.student?.fullName}</div>
                                                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                                                                {r.student?.rollNo ? `Roll: ${r.student.rollNo} · ` : ""}{r.student?.enrollmentNo}
                                                            </div>
                                                        </td>
                                                        <td style={{ fontSize: 12 }}>
                                                            {courseStr} {r.student?.courseYear || ""}
                                                        </td>
                                                        <td style={{ fontSize: 12 }}>{r.installmentLabel}</td>
                                                        <td style={{ fontWeight: 600, color: "#16a34a" }}>{inr(r.amountPaid)}</td>
                                                        <td style={{ fontSize: 12 }}>{r.paymentDate}</td>
                                                        <td>
                                                            <span style={{
                                                                background: (MODE_COLORS[r.paymentMode] || "#6b7280") + "22",
                                                                color: MODE_COLORS[r.paymentMode] || "#6b7280",
                                                                padding: "2px 7px", borderRadius: 99, fontSize: 11.5, fontWeight: 500,
                                                            }}>
                                                                {r.paymentMode}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                            {r.chequeNo || r.transactionRef || "—"}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ background: "#f8fafc" }}>
                                                <td colSpan={5} style={{ fontWeight: 700, fontSize: 13, padding: "0.75rem 1rem" }}>
                                                    Total — {totalReceipts} receipt(s)
                                                </td>
                                                <td style={{ fontWeight: 700, fontSize: 14, color: "#16a34a", padding: "0.75rem 1rem" }}>
                                                    {inr(grandTotal)}
                                                </td>
                                                <td colSpan={3} />
                                            </tr>
                                            {/* Mode-wise breakdown in footer */}
                                            {modeEntries.filter(([, amt]) => amt > 0).map(([mode, amt]) => (
                                                <tr key={mode} style={{ background: "#fafafa" }}>
                                                    <td colSpan={5} style={{ fontSize: 12, color: "var(--text-muted)", paddingLeft: "1rem" }}>
                                                        {mode}
                                                    </td>
                                                    <td style={{ fontSize: 12, fontWeight: 600, color: MODE_COLORS[mode] || "#6b7280", padding: "0.4rem 1rem" }}>
                                                        {inr(amt)}
                                                    </td>
                                                    <td colSpan={3} />
                                                </tr>
                                            ))}
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* ── Signature Line (Print Only) ── */}
                        <div className="only-print" style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: "2.5rem",
                            paddingTop: "1rem",
                            pageBreakInside: "avoid"
                        }}>
                            <div style={{ textAlign: "center", width: "200px" }}>
                                <div style={{ borderTop: "1px solid #000", paddingTop: "5px", fontWeight: 700, fontSize: "11px" }}>
                                    Prepared / Accountant
                                </div>
                            </div>
                            <div style={{ textAlign: "center", width: "200px" }}>
                                <div style={{ borderTop: "1px solid #000", paddingTop: "5px", fontWeight: 700, fontSize: "11px" }}>
                                    Principal / Authorized Signatory
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}